import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../assets/scss/Profile.module.scss';
import settingStyles from '../../assets/scss/Settings/Settings.module.scss';
import { getUserInfo } from '../../utils/user.util';
import { useGetUserByIdQuery } from '../../slices/admin/admin';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BaseURL } from '../../lib/api';

const PaymentSettings = () => {
    const user = getUserInfo();
    const userId = user?._id;
    const navigate = useNavigate();

    // --- Permission Logic ---
    const userPermissions = [];
    user?.roles?.forEach(role => {
        role?.permissions?.forEach(p => {
            if (p?.isActive && p?.functionality === 'paymentSettings') {
                userPermissions.push(p.permissionType?.toLowerCase());
            }
        });
    });

    // SuperAdmin or specific roles might have explicit full access if not restricted by permissions
    // But usually for employees, it's based on permission functionality
    const hasPermission = userPermissions.length > 0 || (user?.type?.toLowerCase() === 'superadmin');
    const isReadOnly = userPermissions.includes('view only') &&
        !userPermissions.includes('fullaccess') &&
        !userPermissions.includes('update') &&
        !userPermissions.includes('create') &&
        user?.type?.toLowerCase() !== 'superadmin';

    const { data, isLoading, error, refetch } = useGetUserByIdQuery(userId, { skip: !userId || !hasPermission });

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        userName: '',
        organizationName: '',
        address1: '',
        address2: '',
        pincode: '',
        city: '',
        state: '',
        gst: '',
        paymentDetails: 'bank',
        bankName: '',
        bankAccount: '',
        confirmBankAccount: '',
        bankIfsc: '',
        upiId: '',
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (data?.result?.length > 0) {
            const u = data.result[0];
            const bank = u.banks?.[0] || {};
            const store = u.store || {};

            setFormData((prev) => ({
                ...prev,
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                email: u.email || '',
                phone: u.phone || '',
                userName: u.userName || '',
                organizationName: store.storeName || '',
                gst: store.gst || '',
                address1: store.address1 || '',
                address2: store.address2 || '',
                pincode: store.pincode?.toString() || '',
                city: store.city || '',
                state: store.state || '',
                bankName: bank.bankName || '',
                bankAccount: bank.accountNo || '',
                confirmBankAccount: bank.accountNo || '',
                bankIfsc: bank.ifsc || '',
                upiId: bank.upiId || '',
                paymentDetails: bank.paymentType || 'bank',
            }));
        }
    }, [data]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};

        // Only validate fields based on payment type
        if (formData.paymentDetails?.toLowerCase() === 'bank') {
            if (!formData.bankName) newErrors.bankName = 'Bank name is required';
            if (!formData.bankAccount) newErrors.bankAccount = 'Account number is required';
            if (!formData.confirmBankAccount) newErrors.confirmBankAccount = 'Confirm account number is required';
            if (!formData.bankIfsc) newErrors.bankIfsc = 'IFSC code is required';
        } else if (formData.paymentDetails?.toLowerCase() === 'upi') {
            if (!formData.upiId) newErrors.upiId = 'UPI ID is required';
        }

        return newErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            const payload = new FormData();

            // Basic fields
            payload.append('firstName', formData.firstName);
            payload.append('lastName', formData.lastName);
            payload.append('userName', formData.userName);
            payload.append('email', formData.email || '');
            payload.append('phone', formData.phone);

            // Address fields so they aren't lost
            payload.append('storeName', formData.organizationName || '');
            payload.append('address1', formData.address1 || '');
            payload.append('address2', formData.address2 || '');
            payload.append('pincode', formData.pincode || '');
            payload.append('city', formData.city || '');
            payload.append('state', formData.state || '');
            payload.append('gst', formData.gst || '');

            // Payment fields
            payload.append('bankName', formData.bankName || '');
            payload.append('accountNo', formData.bankAccount || '');
            payload.append('ifsc', formData.bankIfsc || '');
            payload.append('paymentType', formData.paymentDetails?.toLowerCase() || '');
            payload.append('upiId', formData.upiId || '');

            try {
                const response = await axios.put(
                    `${BaseURL}/user/updateProfile/${userId}`,
                    payload,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${user?.token}`,
                        },
                    }
                );

                toast.success(response.data.message || 'Payment Information updated successfully');
                refetch();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to update payment information');
            }
        }
    };

    if (!hasPermission) {
        return (
            <div className={styles.profileContainerinside} style={{ marginTop: '20px', textAlign: 'center', padding: '50px' }}>
                <h2 style={{ color: '#dc3545' }}>Access Denied</h2>
                <p>You do not have permission to view or manage Payment Settings. Please contact your administrator.</p>
            </div>
        );
    }

    if (isLoading) return <p>Loading payment information...</p>;
    if (error) return <p>Error loading payment information.</p>;

    return (
        <div className={styles.profileContainerinside} style={{ marginTop: '20px' }}>
            <div className={settingStyles.headerRow}>
                <h2 className={settingStyles.header}>Payment Settings</h2>
            </div>

            <div className={styles.card}>
                <div className={styles.header}>
                    <h3>💳 Payment Information</h3>
                </div>

                <div className={styles.content}>
                    <div className={styles.inputGroupheaders}>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Payment Details</label>
                            <select
                                name="paymentDetails"
                                value={formData.paymentDetails}
                                onChange={handleChange}
                                className={styles.inputs}
                                disabled={isReadOnly}
                            >
                                <option value="bank">Bank Account</option>
                                <option value="upi">UPI</option>
                            </select>
                        </div>
                    </div>
                </div>

                {formData.paymentDetails?.toLowerCase() === 'bank' && (
                    <div>
                        <h4 className={styles.subHead}>Bank Details</h4>
                        <div className={styles.inputGroupheader}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Bank Name</label>
                                <input
                                    type="text"
                                    name="bankName"
                                    value={formData.bankName}
                                    onChange={handleChange}
                                    placeholder="Bank Name"
                                    className={styles.input}
                                    disabled={isReadOnly}
                                />
                                {errors.bankName && <span className={styles.error}>{errors.bankName}</span>}
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Bank Account No</label>
                                <input
                                    type="password"
                                    name="bankAccount"
                                    value={formData.bankAccount}
                                    onChange={handleChange}
                                    placeholder="Bank Account No"
                                    className={styles.input}
                                    disabled={isReadOnly}
                                />
                                {errors.bankAccount && <span className={styles.error}>{errors.bankAccount}</span>}
                            </div>
                        </div>

                        <div className={styles.inputGroupheader}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Bank IFSC</label>
                                <input
                                    type="text"
                                    name="bankIfsc"
                                    value={formData.bankIfsc}
                                    onChange={handleChange}
                                    placeholder="Bank IFSC"
                                    className={styles.input}
                                    disabled={isReadOnly}
                                />
                                {errors.bankIfsc && <span className={styles.error}>{errors.bankIfsc}</span>}
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Confirm Bank Account No</label>
                                <input
                                    type="password"
                                    name="confirmBankAccount"
                                    value={formData.confirmBankAccount}
                                    onChange={handleChange}
                                    placeholder="Confirm Bank Account No"
                                    className={styles.input}
                                    disabled={isReadOnly}
                                />
                                {errors.confirmBankAccount && <span className={styles.error}>{errors.confirmBankAccount}</span>}
                            </div>
                        </div>
                    </div>
                )}

                {formData.paymentDetails?.toLowerCase() === 'upi' && (
                    <div>
                        <h4 className={styles.subHead}>UPI Details</h4>
                        <div className={styles.inputGroupheaders}>
                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>UPI ID</label>
                                <input
                                    type="text"
                                    name="upiId"
                                    value={formData.upiId}
                                    onChange={handleChange}
                                    placeholder="UPI ID"
                                    className={styles.input}
                                    disabled={isReadOnly}
                                />
                                {errors.upiId && <span className={styles.error}>{errors.upiId}</span>}
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.buttonGroup} style={{ marginTop: '20px' }}>
                    <button className={styles.cancelButton} onClick={() => navigate('/dashboard')}>Cancel</button>
                    {!isReadOnly && (
                        <button className={styles.saveButton} onClick={handleSubmit}>
                            Save Changes
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentSettings;
